import {chain, isEmpty} from 'lodash';
import React, {FC, Fragment, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {intersperseNodes} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {TeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {TagModel} from '../../../models/api/tagModel';
import {getCompanyUrl} from '../../../store/data/company/companySelectors';
import {BaseFilterDropdownProps} from '../../common/filters/common/types';
import {ModalFilterDropdown} from '../../common/filters/modalFilter/modalFilterDropdown';
import {computeFilterDropdownSourceAsync} from '../../common/filters/sourceFilter/filterDropdownSource';
import {ResourceMultiSelectInput} from '../../common/resourceMultiSelectInput/resourceMultiSelectInput';
import {buildFetcher, buildRowGenerator} from './teammateFilterHelpers';
import {LensFormMultiselectProps} from './types';

/*
 * Props.
 */

interface LensFormAssigneesModalFilterDropdownProps
  extends Omit<LensFormMultiselectProps<TagModel>, 'choices'>,
    BaseFilterDropdownProps {
  choices: ReadonlyArray<TeammateModel>;
  excludedSelectedIds: ReadonlyArray<number>;
  onSetExcludedItemIds: (arg: ReadonlyArray<number>) => void;
  selectedInboxIds?: ReadonlyArray<number>;
  shouldFilterTeammatesByInboxAccess?: boolean;
  selectedInboxesInState: ReadonlyArray<InboxModel>;
}

/*
 * Style.
 */

export const StyledValueWrapper = styled.div`
  display: flex;
  gap: 4px;
  ${ellipsis()}
`;

const StyledTeammateAvatar = styled(TeammateAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
`;

/*
 * Component.
 */

export const LensFormAssigneesModalFilterDropdown: FC<LensFormAssigneesModalFilterDropdownProps> = (
  props,
) => {
  const {
    selectedIds,
    onSetItemIds,
    excludedSelectedIds,
    onSetExcludedItemIds,
    choices,
    selectedInboxesInState,
    selectedInboxIds,
    shouldFilterTeammatesByInboxAccess,
  } = props;
  const [localSelectedIds, setLocalSelectedIds] = useState<ReadonlyArray<number>>(selectedIds);
  const [exclude, setExclude] = useState<boolean>(!isEmpty(excludedSelectedIds));
  const [localExcludedSelectedIds, setLocalExcludedSelectedIds] =
    useState<ReadonlyArray<number>>(excludedSelectedIds);
  const theme = useUserTheme();
  const companyUrl = useAppSelector(getCompanyUrl);

  const dropdownSource = useMemo(() => {
    const fetcher = buildFetcher(
      choices,
      exclude ? excludedSelectedIds : selectedIds,
      companyUrl,
      selectedInboxesInState,
      selectedInboxIds,
      shouldFilterTeammatesByInboxAccess,
    );
    return computeFilterDropdownSourceAsync(fetcher);
  }, [
    choices,
    companyUrl,
    exclude,
    excludedSelectedIds,
    selectedIds,
    selectedInboxIds,
    selectedInboxesInState,
    shouldFilterTeammatesByInboxAccess,
  ]);

  const renderAssigneeSelectionDropdown = () => (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={localSelectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={setLocalSelectedIds}
      onClearAll={() => setLocalSelectedIds([])}
      renderValue={() => renderAssigneeSelectionValue(localSelectedIds, false, choices, theme)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-assignees-placeholder">Select teammates</Trans>
      )}
      dropdownTitle={<Trans id="lens-form-multiselect-assignees-dropdown-title">Teammates</Trans>}
      isDisabled={exclude}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-assignees-disabled">Currently excluding assignees.</Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );

  const renderAssigneeExclusionDropdown = () => (
    <ResourceMultiSelectInput
      dropdownSource={dropdownSource}
      selectedItemIds={localExcludedSelectedIds}
      computeFilterGenerator={buildRowGenerator}
      onSetItems={setLocalExcludedSelectedIds}
      onClearAll={() => setLocalExcludedSelectedIds([])}
      renderValue={() => renderAssigneeSelectionValue(localExcludedSelectedIds, true, choices, theme)}
      renderPlaceholder={() => (
        <Trans id="lens-form-multiselect-exclude-assignees-placeholder">Select teammates</Trans>
      )}
      dropdownTitle={<Trans id="lens-form-multiselect-exclude-assignees-dropdown-title">Teammates</Trans>}
      isDisabled={!exclude}
      isRequired={false}
      messageForDisabled={
        <Trans id="lens-form-multiselect-exclude-assignees-disabled">Currently including assignees.</Trans>
      }
      hasError={props.hasError}
      errorMessage={props.errorMessage}
    />
  );

  const onApplyClicked = () => {
    onSetItemIds(localSelectedIds);
    onSetExcludedItemIds(localExcludedSelectedIds);
  };
  const onCancelClicked = () => {};
  const onClearClicked = () => {
    setLocalSelectedIds([]);
    setLocalExcludedSelectedIds([]);
    setExclude(false);
  };

  return (
    <ModalFilterDropdown
      anchor={props.anchor}
      onRequestClose={props.onRequestClose}
      title={<Trans id="lens-form-assignee-filter-modal-title">Assignees</Trans>}
      filters={[
        {
          value: <Trans id="lens-form-assignee-filter-modal-include">Assignee is</Trans>,
          id: 'lens-form-assignee-filter-modal-include',
          isChecked: !exclude,
          onSelect: () => {
            setExclude(false);
            setLocalExcludedSelectedIds([]);
          },
          renderDropdown: renderAssigneeSelectionDropdown,
        },
        {
          value: <Trans id="lens-form-assignee-filter-modal-exclude">Assignee is not</Trans>,
          id: 'lens-form-assignee-filter-modal-exclude',
          isChecked: exclude,
          onSelect: () => {
            setExclude(true);
            setLocalSelectedIds([]);
          },
          renderDropdown: renderAssigneeExclusionDropdown,
        },
      ]}
      footerHandlers={{onApplyClicked, onCancelClicked}}
      onClear={onClearClicked}
      isActive
      shouldCloseOnClear={false}
      clearButtonLabel={<Trans id="lens-form-assignee-filter-modal-clear">Clear all</Trans>}
    />
  );
};

/*
 * Helpers.
 */

export function renderAssigneeSelectionValue(
  selectedIds: ReadonlyArray<number>,
  exclude: boolean,
  choices: ReadonlyArray<TeammateModel>,
  theme: UserTheme,
) {
  if (!selectedIds.length) {
    return null;
  }

  const selectedTeammates = chain(selectedIds)
    .map((id) => choices.find((teammate) => teammate.id === id))
    .compact()
    .value();

  return (
    <StyledValueWrapper>
      {intersperseNodes(
        selectedTeammates.map((teammate) => {
          const icon = exclude ? (
            <Icon
              name="notAssignSmall"
              color={teammate.avatarUrl ? theme.greys.shade70 : theme.palette[teammate.color].shade40}
            />
          ) : (
            <StyledTeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />
          );
          return {
            node: (
              <Fragment key={`teammate-${teammate.id}`}>
                {icon} {buildTeammateDisplayName(teammate)}
              </Fragment>
            ),
            key: String(teammate.id),
          };
        }),
        () => ', ',
      )}
    </StyledValueWrapper>
  );
}
